import { useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { ChevronRight, Plus, RefreshCw, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { apiErrorMessage } from "@/lib/api";
import { can, requiredRole } from "@/lib/permissions";
import {
  useCustomerQuery,
  useCustomersQuery,
  useCurrentIdentityQuery,
  useLicenseIndexQuery,
} from "@/lib/queries";
import { useTimezone } from "@/lib/timezone";
import { adminCustomerPath, adminLicensePath } from "@/lib/navigation";
import type { Customer, LicenseListItem } from "@/lib/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  EmptyState,
  InlineAlert,
  LoadingRows,
} from "@/components/admin/feedback";
import { PermissionHint } from "@/components/admin/status";
import { LicenseStatusBadge } from "@/components/admin/status";
import { SelectableRow } from "@/components/admin/selectable-row";
import { PageFrame } from "@/components/admin/page-frame";
import { PageHeader } from "@/components/admin/page-header";

export function CustomersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const identityQuery = useCurrentIdentityQuery();
  const customersQuery = useCustomersQuery();
  const customers = customersQuery.data?.customers ?? [];
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState("");
  const selectedID = searchParams.get("customer") ?? "";
  const visibleCustomers = customers.filter((customer) =>
    `${customer.id} ${customer.name}`.toLowerCase().includes(filter.trim().toLowerCase()),
  );
  const selectedIDWithFallback =
    visibleCustomers.some((customer) => customer.id === selectedID)
      ? selectedID
      : visibleCustomers[0]?.id || "";
  const customerQuery = useCustomerQuery(selectedIDWithFallback);
  const allowed = can(identityQuery.data?.roles, "createRecords");

  return (
    <PageFrame size="fluid">
      <PageHeader
        title={t("customers.title", "Customers")}
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => void customersQuery.refetch()}
              disabled={customersQuery.isFetching}
            >
              <RefreshCw
                className="size-4"
                aria-hidden="true"
              />
              {customersQuery.isFetching ? t("common.refreshing", "Refreshing…") : t("common.refresh", "Refresh")}
            </Button>
            <Button
              variant="primary"
              disabled={!allowed}
              onClick={() => navigate("/admin/customers/new")}
            >
              <Plus className="size-4" aria-hidden="true" />
              {t("customers.create", "Create customer")}
            </Button>
          </>
        }
      />
      {!allowed && (
        <PermissionHint
          role={requiredRole("createRecords")}
          action="create customers"
        />
      )}
      {customersQuery.isError ? (
        <InlineAlert
          title={t("customers.loadError", "Customers could not load")}
          message={apiErrorMessage(customersQuery.error)}
          onRetry={() => void customersQuery.refetch()}
        />
      ) : (
        <section
          className="flex min-h-0 flex-1 flex-col rounded-lg border border-line bg-panel overflow-hidden"
          aria-labelledby="customers-heading"
        >
          <div className="flex shrink-0 items-center justify-between border-b border-line px-5 py-3.5">
            <div className="flex items-center gap-3">
              <span className="flex size-8 items-center justify-center rounded-md border border-line bg-panel-raised text-muted">
                <Users className="size-4" aria-hidden="true" />
              </span>
              <div>
                <h2 id="customers-heading" className="font-semibold text-ink">
                  {t("customers.roster", "Customer roster")}
                </h2>
                <p className="mt-0.5 text-xs text-muted">
                  {t("common.records", {
                    count: customers.length,
                    defaultValue: `${customers.length} records returned`,
                  })}
                </p>
              </div>
            </div>
            <Badge variant="info">
              {identityQuery.data?.roles.join(" / ") ?? t("nav.accessPending", "Access pending")}
            </Badge>
          </div>
          {customers.length > 0 && (
            <div className="shrink-0 border-b border-line p-3">
              <Input
                value={filter}
                onChange={(event) => setFilter(event.target.value)}
                placeholder={t("customers.filterPlaceholder", "Filter by customer name or external ref")}
                aria-label="Filter customers"
              />
            </div>
          )}
          {customersQuery.isPending ? (
            <div className="p-4"><LoadingRows /></div>
          ) : customers.length === 0 ? (
            <EmptyState
              title={t("customers.noCustomers", "No customers returned")}
              message={t("customers.noCustomersDesc", "Create a customer record to assign commercial license ownership.")}
              action={
                allowed ? (
                  <Button
                    variant="primary"
                    onClick={() => navigate("/admin/customers/new")}
                  >
                    {t("customers.create", "Create customer")}
                  </Button>
                ) : undefined
              }
            />
          ) : visibleCustomers.length === 0 ? (
            <EmptyState
              title={t("customers.noMatch", "No matching customers")}
              message={t("customers.noMatchDesc", "Try a different customer name or external ref.")}
            />
          ) : (
            <div className="flex-1 min-h-0 grid divide-y divide-line md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] md:divide-x md:divide-y-0 overflow-hidden">
              <div className="flex-1 min-h-0 overflow-y-auto console-scrollbar divide-y divide-line">
                {visibleCustomers.map((customer) => (
                  <SelectableRow
                    className="flex items-center justify-between gap-4 px-5 py-4"
                    key={customer.id}
                    onClick={() => setSearchParams({ customer: customer.id })}
                    selected={customer.id === selectedIDWithFallback}
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium text-ink">
                        {customer.name}
                      </span>
                      <span className="mt-1 block truncate font-mono text-xs text-muted">
                        {customer.id}
                      </span>
                    </span>
                    <span className="flex items-center gap-2">
                      <Badge
                        variant={
                          customer.status === "active" ? "success" : "neutral"
                        }
                      >
                        {customer.status}
                      </Badge>
                      <ChevronRight
                        className="size-4 text-muted"
                        aria-hidden="true"
                      />
                    </span>
                  </SelectableRow>
                ))}
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto console-scrollbar">
                <CustomerDetail
                  customer={customerQuery.data}
                  loading={customerQuery.isPending}
                  error={customerQuery.error}
                />
              </div>
            </div>
          )}
        </section>
      )}
    </PageFrame>
  );
}

function CustomerDetail({
  customer,
  loading,
  error,
}: {
  customer: Customer | undefined;
  loading: boolean;
  error: unknown;
}) {
  const { t } = useTranslation();
  const { formatDate, formatDateTime } = useTimezone();
  const navigate = useNavigate();
  const location = useLocation();
  const licensesQuery = useLicenseIndexQuery(
    "",
    "all",
    Boolean(customer),
    { customer_id: customer?.id },
  );
  const licenses = licensesQuery.data?.pages.flatMap((page) => page.licenses) ?? [];
  const loadMore = () => void licensesQuery.fetchNextPage({ cancelRefetch: false });
  if (loading)
    return (
      <div className="p-5">
        <LoadingRows count={2} />
      </div>
    );
  if (error)
    return (
      <div className="p-5">
        <InlineAlert
          title="Customer detail could not load"
          message={apiErrorMessage(error)}
        />
      </div>
    );
  if (!customer)
    return (
      <EmptyState
        title={t("customers.details.selectCustomer", "Select a customer")}
        message={t("customers.details.selectCustomerDesc", "Choose a customer to inspect its directory record.")}
      />
    );
  return (
    <article className="p-5 sm:p-6" aria-labelledby="customer-detail-heading">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.14em] text-muted">
            {t("customers.details.eyebrow", "Customer detail")}
          </p>
          <h3
            id="customer-detail-heading"
            className="mt-2 text-xl font-semibold tracking-tight text-ink"
          >
            {customer.name}
          </h3>
          <p className="mt-1 font-mono text-xs text-muted">{customer.id}</p>
        </div>
        <Badge variant={customer.status === "active" ? "success" : "neutral"}>
          {customer.status}
        </Badge>
      </div>
      <dl className="mt-6 grid gap-4 text-sm sm:grid-cols-2">
        <div>
          <dt className="text-xs uppercase tracking-[0.12em] text-muted">
            Customer ID
          </dt>
          <dd className="mt-1 font-mono text-ink">{customer.id}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-[0.12em] text-muted">
            {t("common.created", "Created")}
          </dt>
          <dd className="mt-1 text-ink">
            {formatDateTime(customer.created_at)}
          </dd>
        </div>
      </dl>
      <section className="mt-6 border-t border-line pt-4" aria-labelledby="customer-licenses-heading">
        <div className="flex items-center justify-between gap-3">
          <h4 id="customer-licenses-heading" className="text-sm font-medium text-ink">
            {t("customers.details.licenses.title", "Licenses")}
          </h4>
          {licenses.length > 0 && (
            <span className="text-xs text-muted">
              {t("customers.details.licenses.count", {
                count: licenses.length,
                defaultValue: `${licenses.length} loaded`,
              })}
            </span>
          )}
        </div>
        <div className="mt-3 divide-y divide-line border-y border-line">
          {licensesQuery.isPending ? (
            <LoadingRows count={2} />
          ) : licensesQuery.isError && licenses.length === 0 ? (
            <div className="py-3">
              <InlineAlert
                title={t("customers.details.licenses.loadError", "Licenses could not load")}
                message={apiErrorMessage(licensesQuery.error)}
                onRetry={() => void licensesQuery.refetch()}
              />
            </div>
          ) : licenses.length === 0 ? (
            <p className="px-1 py-4 text-sm text-muted">
              {t("customers.details.licenses.empty", "No Licenses are linked to this Customer.")}
            </p>
          ) : (
            licenses.map((item) => (
              <CustomerLicenseRow
                item={item}
                key={item.license.id}
                onOpen={() => navigate(adminLicensePath(item.license.id), {
                  state: {
                    ...(location.state && typeof location.state === "object" ? location.state : {}),
                    indexReturnTo: adminCustomerPath(customer.id),
                  },
                })}
                formatDate={formatDate}
              />
            ))
          )}
        </div>
        {(licensesQuery.hasNextPage || licensesQuery.isFetchNextPageError) && (
          <div className="pt-3">
            {licensesQuery.isFetchNextPageError ? (
              <InlineAlert
                title={t("customers.details.licenses.loadMoreError", "More Licenses could not load")}
                message={t("customers.details.licenses.loadMoreErrorDesc", "Loaded Licenses remain available. Retry the next page when ready.")}
                onRetry={loadMore}
              />
            ) : (
              <Button type="button" variant="outline" size="sm" disabled={licensesQuery.isFetchingNextPage} onClick={loadMore}>
                {licensesQuery.isFetchingNextPage
                  ? t("customers.details.licenses.loadingMore", "Loading more…")
                  : t("customers.details.licenses.loadMore", "Load more Licenses")}
              </Button>
            )}
          </div>
        )}
      </section>
    </article>
  );
}

function CustomerLicenseRow({
  item,
  onOpen,
  formatDate,
}: {
  item: LicenseListItem;
  onOpen: () => void;
  formatDate: (value?: string | null) => string;
}) {
  const { t } = useTranslation();
  const alias = item.license.alias?.trim();

  return (
    <button
      type="button"
      className="flex w-full items-center justify-between gap-3 px-1 py-3 text-left hover:bg-panel-raised focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-focus"
      onClick={onOpen}
    >
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium text-ink">
          {alias ?? item.license.id}
        </span>
        <span className="mt-1 block truncate text-xs text-muted">
          {item.product.name} · {item.license.valid_until
            ? formatDate(item.license.valid_until)
            : t("licenses.perpetual", "Perpetual")}
        </span>
        {alias && <span className="mt-1 block truncate font-mono text-[11px] text-muted">{item.license.id}</span>}
      </span>
      <LicenseStatusBadge status={item.license.status} validUntil={item.license.valid_until} />
    </button>
  );
}
